import { useMemo, useReducer, } from 'react';

import reducer, {
	PreviousReducerValues,
	ReducerDispatch,
	ReducerActions,
	GenericStateObject,
	NextReducerValues,
} from '../utils/reducer';

function _getAugmentedDispatcher( dispatch: ReducerDispatch, actions: ReducerActions = {} ): ReducerDispatch {
	Object.assign(
		dispatch,
		{
			update( payload: unknown ) {
				dispatch( {
					action: 'update',
					payload,
				} );
			},
			replace( payload: unknown ) {
				dispatch( {
					action: 'replace',
					payload,
				} );
			},
		}
	);

	for ( const action in actions ) {
		if ( !Object.prototype.hasOwnProperty.call( actions, action ) ) {
			continue;
		}

		dispatch[ action ] = function( payload: unknown ) {
			dispatch( {
				action,
				payload,
			} );
		};
	}

	return dispatch;
}

type UseDefaultReducerOptions = {
	actions?: ReducerActions
}

const DEFAULT_ACTIONS = {};

function useMergedReducerActions( actions: ReducerActions ) {
	const mergedReducer = useMemo(
		() => function mergedReducer( previous: GenericStateObject, next: NextReducerValues ) {
			const {
				action,
				payload,
			} = next;
			const {
				[ action ]: cta,
			} = actions;

			if ( !cta || typeof cta !== 'function' ) {
				return reducer( previous, next );
			}

			return cta( previous, payload );
		},
		[
			actions,
		]
	);

	return mergedReducer;
}

export default function useDefaultReducer( initialState: PreviousReducerValues, options: UseDefaultReducerOptions = {} ): [ any, ReducerDispatch] {
	const {
		actions = DEFAULT_ACTIONS,
	} = options;
	const reducer = useMergedReducerActions( actions );
	const [
		state,
		dispatch,
	] = useReducer( reducer, initialState );
	const dispatchPlus = useMemo(
		() => _getAugmentedDispatcher( dispatch as ReducerDispatch, actions ),
		[
			dispatch,
			actions,
		]
	);
	return [
		state,
		dispatchPlus,
	];
}
